/**
 * Renders an `OutputValidationReport` (PHASE3-CONTRACT §4): summary counts + grouped SpecIssues
 * with severity chips, field/row/value, and plain-English suggestions. Reused by Generate / Batch /
 * job-detail. Matches the chip + issue styling used by features/editor/ValidationStrip.
 */
import { useTranslation } from "react-i18next";
import { Badge } from "../Badge";
import { localizeIssue } from "../../i18n/issues";
import type { OutputValidationReport, SpecIssue } from "../../api/types";

function IssueItem({ issue, idx }: { issue: SpecIssue; idx: number }) {
  const { t } = useTranslation();
  const meta: string[] = [];
  if (issue.field) meta.push(issue.field);
  if (issue.row != null) meta.push(t("validation.row", { row: issue.row }));
  if (issue.value != null && issue.value !== "")
    meta.push(t("validation.value", { value: issue.value }));
  return (
    <li
      key={`${issue.severity}-${idx}`}
      className={`issue issue--${issue.severity === "error" ? "error" : "warning"}`}
    >
      <span>
        {meta.length > 0 && (
          <span className="mono" style={{ marginRight: "var(--space-2)" }}>
            [{meta.join(" · ")}]
          </span>
        )}
        {localizeIssue(t, issue)}
      </span>
      {issue.suggestion && <span className="issue__suggestion">{issue.suggestion}</span>}
    </li>
  );
}

export function OutputValidationView({
  report,
  title,
}: {
  report: OutputValidationReport;
  title?: string;
}) {
  const { t } = useTranslation();
  const heading = title ?? t("validation.title");
  const { summary } = report;
  const errors = report.issues.filter((i) => i.severity === "error");
  const warnings = report.issues.filter((i) => i.severity !== "error");

  return (
    <div className="stack" style={{ gap: "var(--space-2)" }}>
      <div className="row row--wrap" style={{ gap: "var(--space-2)" }}>
        <strong>{heading}</strong>
        {report.spec_name && <Badge tone="accent">{report.spec_name}</Badge>}
        {report.ok ? (
          <Badge tone="success">{t("validation.passes")}</Badge>
        ) : (
          <>
            {summary.errors > 0 && (
              <Badge tone="danger">{t("validation.errors", { count: summary.errors })}</Badge>
            )}
            {summary.warnings > 0 && (
              <Badge tone="warning">{t("validation.warnings", { count: summary.warnings })}</Badge>
            )}
          </>
        )}
      </div>

      {!report.spec_id && report.issues.length === 0 ? (
        <p className="faint" style={{ margin: 0 }}>
          {report.issues[0]?.message ?? t("validation.noSpecMatched")}
        </p>
      ) : (
        <p className="faint" style={{ margin: 0 }}>
          {t("validation.checkedSummary", {
            count: summary.rows_checked,
            rows: summary.rows_checked,
            fields: summary.fields_checked,
          })}
        </p>
      )}

      {report.issues.length > 0 && (
        <ul className="issue-list">
          {errors.map((issue, i) => (
            <IssueItem key={`e${i}`} issue={issue} idx={i} />
          ))}
          {warnings.map((issue, i) => (
            <IssueItem key={`w${i}`} issue={issue} idx={i} />
          ))}
        </ul>
      )}
    </div>
  );
}
